import { type ReactNode, useEffect, useMemo, useRef } from "react";

import { fieldGridClass } from "../../requests/components/parts";
import { Button } from "../../ui/button";
import { FieldGroup, SelectField, TextField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import { RadioField } from "../../ui/radio";
import type { ItineraryFailure } from "../errors";
import type { FieldErrors, TimingForm, WallTimeForm } from "../operations";
import { formatOffset, knownZoneNames, offsetMinutesAt, wallTimeKind } from "../time";

// The wall-time inputs. A time is a date, a clock time and an explicit IANA zone: the device zone is never read. The server converts
// the wall time to an instant and is the judge of a gap or a repeated time. The page probes the zone only to place a message early:
// a clock time that does not exist (the clocks jump over it) shows the first valid time that the server sent back; a clock time that
// happens twice shows the earlier/later choice, and the choice goes to the server as `disambiguation`.

export function InputField({
  id,
  label,
  type,
  value,
  onChange,
  error,
  hint,
  list,
  required,
  min,
  max,
  placeholder,
}: Readonly<{
  id: string;
  label: string;
  type: "date" | "time" | "text" | "number";
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: ReactNode;
  list?: string;
  required?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
}>) {
  return (
    <TextField
      id={id}
      label={label}
      type={type}
      value={value}
      onChange={onChange}
      error={error}
      hint={hint}
      {...(list === undefined ? {} : { list })}
      {...(required === undefined ? {} : { required })}
      {...(min === undefined ? {} : { min })}
      {...(max === undefined ? {} : { max })}
      {...(placeholder === undefined ? {} : { placeholder })}
    />
  );
}

const ZONE_LIST_ID = "itinerary-zone-names";

/** One `<datalist>` of the IANA zone names the runtime knows, shared by every zone field of a dialog. */
export function ZoneDatalist() {
  const names = useMemo(() => knownZoneNames(), []);
  return (
    <datalist id={ZONE_LIST_ID}>
      {names.map((name) => (
        <option key={name} value={name} />
      ))}
    </datalist>
  );
}

const timePart = (suggestion: string) => suggestion.slice(11, 16);

type WallTimeProps = Readonly<{
  idPrefix: string;
  legend: string;
  value: WallTimeForm;
  onChange: (next: WallTimeForm) => void;
  errors: FieldErrors;
  failure: ItineraryFailure | null;
  /** This field carries the server's gap suggestion (the first field that is a gap, else the start). */
  takesGapSuggestion: boolean;
  /** This field carries the server's repeated-time refusal. */
  takesChoice: boolean;
  zoneLabel?: string;
}>;

export function WallTimeFields({
  idPrefix,
  legend,
  value,
  onChange,
  errors,
  failure,
  takesGapSuggestion,
  takesChoice,
  zoneLabel = "Time zone",
}: WallTimeProps) {
  // Changing the date, the time or the zone makes an earlier choice meaningless: the user chooses again if the time still repeats.
  const set = (patch: Partial<WallTimeForm>) =>
    onChange({ ...value, ...patch, disambiguation: patch.disambiguation ?? "reject" });
  const probe = wallTimeKind(value.date, value.time, value.timeZone.trim());
  const showGap = probe.kind === "GAP" || (takesGapSuggestion && failure?.kind === "time_gap");
  const showChoice =
    probe.kind === "OVERLAP" || (takesChoice && failure?.kind === "time_ambiguous");

  // When the server answers with a gap or a repeated time, move the user to the control that fixes it: the dialog can be long, and
  // the server's message sits at its end.
  const choiceRef = useRef<HTMLInputElement>(null);
  const suggestionRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (takesChoice && failure?.kind === "time_ambiguous") choiceRef.current?.focus();
    else if (takesGapSuggestion && failure?.kind === "time_gap") suggestionRef.current?.focus();
  }, [failure, takesChoice, takesGapSuggestion]);
  const suggestion = takesGapSuggestion && failure?.kind === "time_gap" ? failure.suggestion : null;
  const offsetOf = (index: number): string => {
    const instant = probe.instants[index];
    return instant === undefined
      ? ""
      : ` (${formatOffset(offsetMinutesAt(instant, value.timeZone.trim()))})`;
  };

  return (
    <FieldGroup legend={legend}>
      <div className={fieldGridClass}>
        <InputField
          id={`${idPrefix}-date`}
          label="Date"
          type="date"
          value={value.date}
          onChange={(date) => set({ date })}
          error={errors[`${idPrefix}-date`]}
          required
        />
        <InputField
          id={`${idPrefix}-time`}
          label="Time"
          type="time"
          value={value.time}
          onChange={(time) => set({ time })}
          error={errors[`${idPrefix}-time`]}
          required
        />
        <InputField
          id={`${idPrefix}-zone`}
          label={zoneLabel}
          type="text"
          value={value.timeZone}
          onChange={(timeZone) => set({ timeZone })}
          error={errors[`${idPrefix}-zone`]}
          list={ZONE_LIST_ID}
          placeholder="Europe/London"
          required
        />
      </div>
      {showGap ? (
        <Notice tone="warning" role="status">
          <p>
            {value.time} on {value.date} does not exist in {value.timeZone}: the clocks jump forward
            over it.
          </p>
          {suggestion === null ? (
            <p>The server will refuse it. Choose a different time.</p>
          ) : (
            <>
              <p>
                The first valid time is {timePart(suggestion)} on {suggestion.slice(0, 10)}.
              </p>
              <Button
                ref={suggestionRef}
                variant="outline"
                size="sm"
                onClick={() =>
                  set({
                    date: suggestion.slice(0, 10),
                    time: timePart(suggestion),
                    disambiguation: "reject",
                  })
                }
              >
                Use {timePart(suggestion)}
              </Button>
            </>
          )}
        </Notice>
      ) : null}
      {showChoice ? (
        <FieldGroup
          legend={`${value.time} on ${value.date} happens twice in ${value.timeZone}. Which one do you mean?`}
        >
          {(["earlier", "later"] as const).map((choice, index) => (
            <RadioField
              key={choice}
              inputRef={index === 0 ? choiceRef : undefined}
              id={`${idPrefix}-choice-${choice}`}
              name={`${idPrefix}-choice`}
              checked={value.disambiguation === choice}
              onChange={() => onChange({ ...value, disambiguation: choice })}
              label={
                choice === "earlier"
                  ? `The earlier one, before the clocks go back${offsetOf(index)}`
                  : `The later one, after the clocks go back${offsetOf(index)}`
              }
            />
          ))}
        </FieldGroup>
      ) : null}
    </FieldGroup>
  );
}

type TimingProps = Readonly<{
  value: TimingForm;
  onChange: (next: TimingForm) => void;
  errors: FieldErrors;
  failure: ItineraryFailure | null;
  /** The zone and date a new start begins with. */
  defaults: Readonly<{ date: string; timeZone: string }>;
  allowEnd: boolean;
}>;

const TIMING_OPTIONS = [
  { value: "SCHEDULED", label: "At a time" },
  { value: "ALL_DAY", label: "All day" },
  { value: "UNTIMED", label: "No time yet" },
] as const;

/** Which wall-time field the server's gap or repeated-time refusal belongs to: the first one the zone data agrees on, else the start. */
function targetsOf(value: TimingForm): Readonly<{ gap: "start" | "end"; choice: "start" | "end" }> {
  if (value.kind !== "SCHEDULED") return { gap: "start", choice: "start" };
  const kindOf = (form: WallTimeForm) =>
    wallTimeKind(form.date, form.time, form.timeZone.trim()).kind;
  const startKind = kindOf(value.start);
  const endKind = value.endMode === "END" ? kindOf(value.end) : "UNIQUE";
  return {
    gap: startKind === "GAP" ? "start" : endKind === "GAP" ? "end" : "start",
    choice: startKind === "OVERLAP" ? "start" : endKind === "OVERLAP" ? "end" : "start",
  };
}

export function TimingFields({
  value,
  onChange,
  errors,
  failure,
  defaults,
  allowEnd,
}: TimingProps) {
  const target = targetsOf(value);
  const changeKind = (kind: string) => {
    if (kind === value.kind) return;
    if (kind === "SCHEDULED") {
      const start: WallTimeForm = {
        date: defaults.date,
        time: "",
        timeZone: defaults.timeZone,
        disambiguation: "reject",
      };
      onChange({ kind, start, endMode: "DURATION", durationMinutes: "60", end: { ...start } });
    } else if (kind === "ALL_DAY" || kind === "UNTIMED") {
      onChange({ kind });
    }
  };
  return (
    <div className="grid gap-4">
      <ZoneDatalist />
      <SelectField
        id="timing-kind"
        label="When"
        value={value.kind}
        onChange={changeKind}
        options={TIMING_OPTIONS}
      />
      {value.kind !== "SCHEDULED" ? null : (
        <>
          <WallTimeFields
            idPrefix="start"
            legend="Starts"
            value={value.start}
            onChange={(start) => onChange({ ...value, start })}
            errors={errors}
            failure={failure}
            takesGapSuggestion={target.gap === "start"}
            takesChoice={target.choice === "start"}
          />
          <FieldGroup legend="How it ends">
            <RadioField
              id="end-mode-duration"
              name="end-mode"
              checked={value.endMode === "DURATION"}
              onChange={() => onChange({ ...value, endMode: "DURATION" })}
              label="After a length of time"
            />
            {allowEnd ? (
              <RadioField
                id="end-mode-end"
                name="end-mode"
                checked={value.endMode === "END"}
                onChange={() => onChange({ ...value, endMode: "END" })}
                label="At a clock time (it may be in another zone, like a flight)"
              />
            ) : null}
          </FieldGroup>
          {value.endMode === "DURATION" || !allowEnd ? (
            <InputField
              id="duration"
              label="Length in minutes"
              type="number"
              min={1}
              max={10080}
              value={value.durationMinutes}
              onChange={(durationMinutes) => onChange({ ...value, durationMinutes })}
              error={errors.duration}
              hint="Elapsed time. A day with a clock change keeps its real length."
            />
          ) : (
            <WallTimeFields
              idPrefix="end"
              legend="Ends"
              zoneLabel="End time zone"
              value={value.end}
              onChange={(end) => onChange({ ...value, end })}
              errors={errors}
              failure={failure}
              takesGapSuggestion={target.gap === "end"}
              takesChoice={target.choice === "end"}
            />
          )}
        </>
      )}
    </div>
  );
}
